import React, { useState, useEffect, useMemo, useRef } from 'react';

import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { FormikProps, useFormikContext } from 'formik';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import _isEmpty from 'lodash/isEmpty';
import { matchPath, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { encode } from 'html-entities';
import { toast } from 'react-toastify';

import { WarningIcon } from 'assets/icons';
import { useAppSelector } from 'hooks';
import { useQuery } from 'hooks';
import { usePrefUnitForMeasurementClass } from 'hooks';
import { getPath } from 'routes';
import { Loading, Status } from 'components/Fetch';
import { questionIcon } from 'assets';
import regex from 'constants/regex';
import instance, { useAxios } from 'configs/requests';
import { getMedicalConditionOptions } from 'constants/constants';
import { genderToNumber, getLongPatientGender } from 'utils/ProfileStoreFunctions';
import {
  BillingTabs,
  InvitationStep,
  InviteType,
  CareProgramTypes,
  ShippingSpeed
} from 'constants/enums';
import StepperPage, { PageStep, FooterControls } from 'components/UI/StepperPage';
import { FloatingFooter } from 'components/UI';
import { ToastNotificationContent } from 'components/UI';
import {
  getAuthorizedSubgroups,
  getCarePrograms,
  getCountryCodeByGid,
  getCurrentOrg,
  getCurrentOrgSubgroups,
  getGroupByGid,
  isPrepaidOrg,
  selectCurrentGid
} from 'store/groups/reducer';
import { fetchDeviceOrderings } from 'store/groups/service';
import { updatePatient } from 'store/patient/reducer';
import { fetchAllMeasurementPages } from 'store/patient/service';
import { getConditionValue, getPreference } from 'utils/Helpers/Patient';
import {
  addressesAreEqual,
  getDefaultAddress,
  isAddressValid,
  shippingAddressToForm,
  toDeviceOrderShippingInfo
} from 'utils/Address';
import { cartHasAmazonItem, deviceNameForSku, resolveCatalogEntry } from 'utils/Devices';
import { buildMeasurement } from 'utils/Measurements';
import { IBodyMeasurementFields, getLatestBodyMeasurements } from 'utils/body-measurements';
import { formatMinor } from 'utils/Currency';
import { getApiErrorMessage } from 'utils/api-error';
import { useRefreshBilling } from 'pages/Dashboard/Billing/hooks';
import { selectBillingBalance } from 'store/billing/reducer';

import {
  SelectGroupAndType,
  DemographicInformation,
  CarePlans,
  Review,
  Devices,
  ShippingInfo,
  MedicalInformation
} from './Steps';
import SelectPrograms from './Steps/SelectPrograms';
import { useStockOrderShippingDefaults } from './useStockOrderShippingDefaults';

export interface IInvitation {
  [key: string]: any;
  gid: string;
  type: InviteType;
  patientInfo: IInvitationPatientInfo;
  medicalInfo: IInvitationMedicalInfo;
  patientCareProgram: string[];
  careplanTemplateIds: string[];
  devices: { [key: string]: number };
  accessories: { [key: string]: number };
  shippingInfo: IShippingInfo;
  /** Amazon MCF speed; only meaningful, and only sent, when the cart holds an Amazon SKU. */
  shippingSpeed: ShippingSpeed;
  /** The chosen speed's arrival window from the preview, repeated on Review. */
  shippingSpeedEstimate: IDeliveryWindow | null;
  bmiChangedFlag: boolean;
}

export interface IInvitationPatientInfo {
  firstName?: string;
  lastName?: string;
  username?: string;
  email_address?: string;
  mobileNumber?: string;
  phoneNumber?: string;
  bday?: string | null;
  gender?: string;
  mrn?: string;
  ssn?: string;
  npi?: string;
  locale?: string;
  isRequiredField: boolean;
}
export interface IShippingInfo {
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  street_address_line_2?: string;
  address: IAddress;
  useAsPatientAddress?: boolean;
}

interface IInvitationMedicalInfo {
  primaryRpmReason: string;
  secondaryRpmReason?: string;
  conditions?: ISelectOption[];
  otherMedicalInfo?: string;
  payer?: string;
  provider?: string;
  height?: string;
  heightInches?: string;
  weight?: string;
  dryWeight?: string;
}

interface IInviteProps {
  inviteType?: InviteType;
  patient?: IPatient;
  bulkOrderDevices?: boolean;
  fallback?: (status: boolean) => any;
}

const seriesToNum = (series: DeviceSeries): number => {
  switch (series) {
    case 'direct_plus':
      return 2;
    case 'direct':
      return 1;
    case 'partner':
      return 0;
  }
};

const addByMClass = (mClass: string, group: Set<DeviceTypeKey>) => {
  if (mClass === 'pressure') group.add('bloodPressureArmKey');
  else if (mClass === 'blood_oxygen' || mClass === 'blood_oxygen_active')
    group.add('oximeterTypeKey');
  else if (mClass === 'weight' || mClass === 'dry_weight') group.add('bodyScaleTypeKey');
  else if (mClass.startsWith('glucose')) group.add('glucometerTypeKey');
};

const addByKeyword = (text: string, group: Set<DeviceTypeKey>) => {
  const t = text.toLowerCase();
  if (t.includes('hypertension')) group.add('bloodPressureArmKey');
  if (t.includes('diabetes')) group.add('glucometerTypeKey');
  if (t.includes('copd')) group.add('oximeterTypeKey');
  if (t.includes('chf')) {
    group.add('bodyScaleTypeKey');
    group.add('bloodPressureArmKey');
    group.add('oximeterTypeKey');
  }
};

const Onboard = ({ inviteType, patient, fallback }: IInviteProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emrpatientId } = useQuery();
  const isEmbedded = useAppSelector((state) => state.settings.embedded);
  const navigationHidden = useAppSelector((state) => state.settings.hideNavigation);
  const isEmbeddedWithPatient = isEmbedded && navigationHidden;
  const selectedGid = useAppSelector(selectCurrentGid);
  const isAddDevice =
    inviteType === InviteType.ORDER_PATIENT_DEVICE ||
    inviteType === InviteType.BULK_ORDER_DEVICES ||
    inviteType === InviteType.BULK_ORDER_ACCESSORIES;
  const heightUnit = usePrefUnitForMeasurementClass('height');
  const weightUnit = usePrefUnitForMeasurementClass('weight');
  const [carePlans, setCarePlans] = useState<ICarePlanTemplate[]>([]);
  const [suggestedDevices, setSuggestedDevices] = useState<IDeviceOrderingCatalogItem[]>([]);
  const [savedMedicalInfo, setSavedMedicalInfo] = useState<IInvitationMedicalInfo | null>(null);
  const [footerHeight, setFooterHeight] = useState(72);
  const [savedCareplanTemplateIds, setSavedCareplanTemplateIds] = useState<string[]>([]);
  const allConditions: ISelectOption[] = getMedicalConditionOptions(intl, patient?.gender || '1');
  const primaryRpmReason = patient?.primaryRpmReason
    ? patient?.conditions.find((condition) => condition[patient.primaryRpmReason] !== undefined)
        ?.condition || ''
    : '';
  const secondaryRpmReason = patient?.secondaryRpmReason
    ? patient?.conditions.find((condition) => condition[patient.secondaryRpmReason] !== undefined)
        ?.condition || ''
    : '';

  const currentOrg = useAppSelector(getCurrentOrg);
  const prepaidOrg = useAppSelector(isPrepaidOrg);
  const { refreshCredits } = useRefreshBilling();
  const balance = useAppSelector(selectBillingBalance).data;
  const currentOrgSubgroups = useAppSelector(getCurrentOrgSubgroups);
  const allSubgroups = useAppSelector(getAuthorizedSubgroups);

  // If the current user has admin access to an org and that org has subgroups, show those
  // If they have access to an org that has no subgroups, show the org
  // If they have access to subgroups and no org, show those subgroups
  const dropdownGroups = currentOrgSubgroups?.length
    ? currentOrgSubgroups
    : currentOrg
      ? [currentOrg]
      : allSubgroups;

  // If the current user has access to only one subgroup, select it by default
  // If they have access to multiple subgroups, select the one that is currently selected
  const defaultInvitationGid =
    dropdownGroups.length === 1
      ? dropdownGroups[0].gid || ''
      : dropdownGroups.find((group) => group.gid === selectedGid)?.gid || '';

  const skipGroupStep = !!(dropdownGroups.length === 1 && defaultInvitationGid);

  /** The order just placed, so the confirmation can name it rather than just saying "done". */
  const [orderResult, setOrderResult] = useState<IDeviceOrderResponse | null>(null);
  const [invitationGid, setInvitationGid] = useState<string>(
    patient
      ? patient?.subgroup_gid || patient?.gid
      : inviteType === InviteType.BULK_ORDER_DEVICES
        ? selectedGid
        : defaultInvitationGid
  );
  const [activeInviteType, setActiveInviteType] = useState<InviteType>(
    inviteType || InviteType.INVITE_ONLY
  );
  const programs = useAppSelector((state) => getCarePrograms(state, invitationGid));
  const isStockOrder =
    activeInviteType === InviteType.BULK_ORDER_DEVICES ||
    activeInviteType === InviteType.BULK_ORDER_ACCESSORIES;
  const stockShippingDefaults = useStockOrderShippingDefaults(isStockOrder ? invitationGid : '');

  const [invitation, setInvitation] = useState<any>(null);
  const [patientNameFull, setPatientNameFull] = useState<string>('');
  const [skipDevices, setSkipDevices] = useState(!isAddDevice);
  const [skipShipping, setSkipShipping] = useState(false);
  const selectedGroupName = useAppSelector((state) => getGroupByGid(state, invitationGid));
  const countryCode = useAppSelector((state) => getCountryCodeByGid(state, invitationGid));
  const deviceAvailableToGroup = useAppSelector((state) => state.groups.deviceOrderingsAvailable);
  const orderableDevices = useMemo(() => {
    if (activeInviteType === InviteType.BULK_ORDER_ACCESSORIES) {
      return deviceAvailableToGroup.filter(
        (d) => !!d.parentDeviceOrderingId && !d.isDefaultAccessory
      );
    }
    return deviceAvailableToGroup.filter((d) => !d.parentDeviceOrderingId);
  }, [deviceAvailableToGroup, activeInviteType]);

  useEffect(() => {
    if (invitationGid) {
      dispatch(fetchDeviceOrderings(invitationGid));
    }
  }, [invitationGid, dispatch]);

  const isMRNMandatory = selectedGroupName?.autoCreateEmrRecord === '1';

  const [{ data: dataAssign, loading: loadingAssign, error: errorAssign }, postPatientsInfo] =
    useAxios<IInvitePatientsResponse>(
      {
        url: '/clinician/Patients',
        method: 'POST'
      },
      { manual: true }
    );

  const [{ loading: loadingDeviceOrder, error: errorDeviceOrder }, postDeviceOrder] = useAxios(
    {
      url: '/clinician-gateway/device/order',
      method: 'POST'
    },
    { manual: true }
  );

  const [bodyMeasurePrefill, setBodyMeasurePrefill] = useState<IBodyMeasurementFields | null>(null);
  const [bodyMeasuresLoading, setBodyMeasuresLoading] = useState(
    inviteType === InviteType.ORDER_PATIENT_DEVICE && !!patient?.uid
  );
  const bodyMeasuresFetchedUidRef = useRef<string | null>(null);
  const bodyMeasurePrefillAppliedRef = useRef(false);
  const focusHeightRef = useRef(false);

  useEffect(() => {
    const uid = patient?.uid;
    if (
      activeInviteType !== InviteType.ORDER_PATIENT_DEVICE ||
      !uid ||
      bodyMeasuresFetchedUidRef.current === uid
    )
      return;
    bodyMeasuresFetchedUidRef.current = uid;
    setBodyMeasuresLoading(true);
    fetchAllMeasurementPages({
      specificUid: uid,
      class: JSON.stringify([{ class: 'height' }, { class: 'weight' }])
    })
      .then((result) =>
        setBodyMeasurePrefill(getLatestBodyMeasurements(result, heightUnit, weightUnit))
      )
      .catch(() => undefined)
      .finally(() => setBodyMeasuresLoading(false));
  }, [activeInviteType, patient?.uid, heightUnit, weightUnit]);

  const submitData = dataAssign;
  const submitLoading = loadingAssign || loadingDeviceOrder;
  const submitError = errorAssign || errorDeviceOrder;

  const [{ data: dataEMRPatient, loading: loadingEMRPatient }] = useAxios(
    {
      url: `/clinician/EmrPatient/${emrpatientId}?emrUseInvite=true`,
      method: 'GET'
    },
    { manual: emrpatientId ? false : true }
  );

  const [
    { data: patientDevices, loading: loadingPatientDevices, error: errorPatientDevices },
    getPatientdevices
  ] = useAxios<IDeviceListItem[]>(
    {
      url: `/clinician-gateway/patient/${patient?.uid}/device`,
      method: 'GET'
    },
    { manual: true }
  );

  const buildBirthday = () => {
    const bday = dataEMRPatient?.birthday || (isAddDevice ? patient?.birthday : '');
    return bday || null;
  };

  // When ordering a device for an existing patient, prefill shipping from their last shipping address.
  const lastShipping = isAddDevice ? patient?.lastShippingAddress : null;
  const lastShippingAddress = lastShipping?.address;

  // Precedence for the prefilled shipping info: EMR data, then last shipping address, then patient.
  const buildShippingContact = () => ({
    firstName:
      dataEMRPatient?.firstName ||
      lastShipping?.firstName ||
      (isAddDevice ? patient?.firstName : '') ||
      '',
    lastName:
      dataEMRPatient?.lastName ||
      lastShipping?.lastName ||
      (isAddDevice ? patient?.lastName : '') ||
      '',
    email:
      dataEMRPatient?.email_address ||
      lastShipping?.email ||
      (isAddDevice ? patient?.email_address : '') ||
      '',
    phoneNumber:
      dataEMRPatient?.primaryPhoneNumber ||
      lastShipping?.phoneNumber ||
      (isAddDevice ? patient?.primaryPhoneNumber : '') ||
      ''
  });

  const buildShippingAddress = (): IAddress =>
    getDefaultAddress({
      city:
        dataEMRPatient?.address?.city || lastShippingAddress?.city || patient?.address?.city || '',
      country_code:
        dataEMRPatient?.address?.country_code ||
        lastShippingAddress?.countryCode ||
        patient?.address?.country_code ||
        '',
      state_province:
        dataEMRPatient?.address?.state_province ||
        lastShippingAddress?.stateProvince ||
        patient?.address?.state_province ||
        '',
      street_address_line_1:
        dataEMRPatient?.address?.street_address_line_1 ||
        lastShippingAddress?.streetAddressLine1 ||
        patient?.address?.street_address_line_1 ||
        '',
      street_address_line_2:
        dataEMRPatient?.address?.street_address_line_2 ||
        lastShippingAddress?.streetAddressLine2 ||
        patient?.address?.street_address_line_2 ||
        '',
      postal_code:
        dataEMRPatient?.address?.postal_code ||
        lastShippingAddress?.postalCode ||
        patient?.address?.postal_code ||
        ''
    });

  const buildShippingInfo = (): IShippingInfo => {
    if (stockShippingDefaults) return { ...stockShippingDefaults, useAsPatientAddress: false };
    const address = buildShippingAddress();
    return {
      ...buildShippingContact(),
      street_address_line_2: address.street_address_line_2,
      address,
      useAsPatientAddress: false
    };
  };

  // Existing patients can have both a home address and a different last shipping address.
  // Default to the last shipping address but let the user toggle between the two.
  const patientHomeAddress = getDefaultAddress(patient?.address);
  const lastShippingAddressForm = shippingAddressToForm(lastShippingAddress);

  const homeShippingCandidate: IShippingInfo = {
    firstName: patient?.firstName || '',
    lastName: patient?.lastName || '',
    email: patient?.email_address || '',
    phoneNumber: patient?.primaryPhoneNumber || '',
    street_address_line_2: patientHomeAddress.street_address_line_2,
    address: patientHomeAddress,
    useAsPatientAddress: false
  };

  const lastShippingCandidate: IShippingInfo = {
    firstName: lastShipping?.firstName || '',
    lastName: lastShipping?.lastName || '',
    email: lastShipping?.email || '',
    phoneNumber: lastShipping?.phoneNumber || '',
    street_address_line_2: lastShippingAddressForm.street_address_line_2,
    address: lastShippingAddressForm,
    useAsPatientAddress: false
  };

  const showAddressToggle =
    isAddDevice &&
    !!isAddressValid(patientHomeAddress) &&
    !!isAddressValid(lastShippingAddressForm) &&
    !addressesAreEqual(patientHomeAddress, lastShippingAddressForm);

  const defaultInvitation: IInvitation = {
    gid: invitationGid,
    type: activeInviteType,
    patientCareProgram: programs.length === 1 ? [programs[0].cpid] : [],
    careplanTemplateIds: [],
    devices: {},
    accessories: {},
    bmiChangedFlag: false,
    patientInfo: {
      firstName: dataEMRPatient?.firstName || (isAddDevice ? patient?.firstName : ''),
      lastName: dataEMRPatient?.lastName || (isAddDevice ? patient?.lastName : ''),
      username: dataEMRPatient?.username || (isAddDevice ? patient?.username : ''),
      email_address:
        dataEMRPatient?.email_address ||
        (isAddDevice && patient?.email_address ? patient?.email_address : ''),
      mobileNumber:
        dataEMRPatient?.MobilePhoneNumber ||
        (isAddDevice && patient?.MobilePhoneNumber ? patient?.MobilePhoneNumber : ''),
      phoneNumber:
        dataEMRPatient?.primaryPhoneNumber ||
        (isAddDevice && patient?.primaryPhoneNumber ? patient?.primaryPhoneNumber : ''),
      bday: buildBirthday(),
      mrn: dataEMRPatient?.alternateId || '',
      ssn: dataEMRPatient?.ssn || '',
      npi: '',
      gender: dataEMRPatient?.gender
        ? getLongPatientGender(dataEMRPatient.gender)
        : isAddDevice
          ? getLongPatientGender(patient?.gender)
          : '',
      locale: isAddDevice ? patient?.locale : '',
      isRequiredField: false
    },
    medicalInfo: {
      primaryRpmReason,
      secondaryRpmReason,
      conditions:
        allConditions.filter(
          (optCondition) =>
            patient?.conditions.some(
              (patientCondition) => patientCondition.condition === optCondition.value
            ) &&
            optCondition.value !== primaryRpmReason &&
            optCondition.value !== secondaryRpmReason
        ) || [],
      otherMedicalInfo: patient?.otherMedicalInfo || '',
      payer: patient?.payer || '',
      provider: dataEMRPatient?.doctor_in_charge || '',
      height: '',
      heightInches: '',
      weight: '',
      dryWeight: ''
    },
    shippingInfo: buildShippingInfo(),
    shippingSpeed: ShippingSpeed.STANDARD,
    shippingSpeedEstimate: null
  };

  const handlePatientParams = (values: IInvitation) => {
    const conditions =
      values.medicalInfo?.conditions?.map((x) => {
        return { [getPreference(x.value)]: getConditionValue(x.value, intl), condition: x.value };
      }) || [];
    if (values.medicalInfo?.primaryRpmReason)
      conditions.push({
        [getPreference(values.medicalInfo?.primaryRpmReason)]: getConditionValue(
          values.medicalInfo?.primaryRpmReason,
          intl
        ),
        condition: values.medicalInfo?.primaryRpmReason
      });
    if (values.medicalInfo?.secondaryRpmReason)
      conditions.push({
        [getPreference(values.medicalInfo?.secondaryRpmReason)]: getConditionValue(
          values.medicalInfo?.secondaryRpmReason,
          intl
        ),
        condition: values.medicalInfo?.secondaryRpmReason
      });
    const patientInfofinal: IInvitePatient = {
      Group: selectedGroupName?.name,
      gid: values.gid,
      firstName: values.patientInfo.firstName,
      lastName: values.patientInfo.lastName,
      username: values.patientInfo.username,
      email_address: values.patientInfo.email_address,
      phoneNumber: values.patientInfo.phoneNumber,
      mobilePhoneNumber: values.patientInfo.mobileNumber,
      mrn: values.patientInfo.mrn,
      ssn: values.patientInfo.ssn,
      npi: values.patientInfo.npi,
      birthday: values.patientInfo.bday
        ? DateTime.fromISO(values.patientInfo.bday).toFormat('yyyy-MM-dd')
        : null,
      gender: genderToNumber(values.patientInfo.gender),
      language: values.patientInfo.locale,
      devices: {}
    };

    if (!isAddDevice) {
      patientInfofinal.careplanTemplateIds = values.careplanTemplateIds;
      patientInfofinal.patientCareProgram = values.patientCareProgram;
      patientInfofinal.primaryRpmReason = values.medicalInfo?.primaryRpmReason
        ? getPreference(values.medicalInfo.primaryRpmReason)
        : '';
      patientInfofinal.secondaryRpmReason = values.medicalInfo?.secondaryRpmReason
        ? getPreference(values.medicalInfo.secondaryRpmReason)
        : '';
      patientInfofinal.conditions = conditions;
      patientInfofinal.otherMedicalInfo = values.medicalInfo?.otherMedicalInfo
        ? encode(values.medicalInfo?.otherMedicalInfo)
        : '';
      patientInfofinal.payer = values.medicalInfo?.payer ? encode(values.medicalInfo?.payer) : '';
      patientInfofinal.doctorInCharge = values.medicalInfo?.provider
        ? encode(values.medicalInfo.provider)
        : '';
    }

    if (values.shippingInfo.address.city.length > 0) {
      patientInfofinal.shippingInfo = {
        firstName: values.shippingInfo.firstName,
        lastName: values.shippingInfo.lastName,
        email: values.shippingInfo.email,
        phoneNumber: values.shippingInfo.phoneNumber
      };
    }
    return patientInfofinal;
  };

  const switchStep = (values: IInvitation) => {
    const shouldSkipDevices =
      (programs.filter(
        (prog) =>
          prog.type === CareProgramTypes.MONITORING && values.patientCareProgram.includes(prog.cpid)
      ).length === 0 &&
        !isAddDevice) ||
      selectedGroupName?.disableDeviceManagement === '1';
    if (values.gid !== invitationGid) {
      setInvitationGid(values.gid);
    }
    setSkipDevices(shouldSkipDevices);
    setSkipShipping(
      Object.keys(values.devices).length === 0 && Object.keys(values.accessories).length === 0
    );
    setActiveInviteType(values.type);
    setPatientNameFull(values.patientInfo.lastName + ', ' + values.patientInfo.firstName);
    setSavedMedicalInfo(values.medicalInfo);
    setSavedCareplanTemplateIds(values.careplanTemplateIds);
  };

  const skipStep = (values: IInvitation, nextStepIndex: number, orderedSteps: PageStep[]) => {
    switch (orderedSteps[nextStepIndex].name) {
      case InvitationStep.DEVICES:
      case InvitationStep.ACCESSORIES:
        return (
          (programs.filter(
            (prog) =>
              prog.type === CareProgramTypes.MONITORING &&
              values.patientCareProgram.includes(prog.cpid)
          ).length === 0 &&
            !isAddDevice) ||
          skipDevices
        );
      case InvitationStep.SHIPPING_INFO:
        return (
          Object.keys(values.devices).length === 0 && Object.keys(values.accessories).length === 0
        );
      default:
        return false;
    }
  };

  const buildBaselineMeasurements = (uid: string, medicalInfo: IInvitationMedicalInfo) => {
    const now = DateTime.utc().toFormat('yyyy-MM-dd HH:mm:ss');
    const measurements: IMeasurementPost[] = [];

    if (medicalInfo.height) {
      const unit = heightUnit || 'm';
      let heightValue = medicalInfo.height;
      if (unit === 'ft') {
        const feet = parseFloat(medicalInfo.height);
        const inches = parseInt(medicalInfo.heightInches || '0', 10);
        heightValue = (feet + inches / 12).toFixed(4);
      }
      measurements.push(
        buildMeasurement(
          'height',
          now,
          0,
          uid,
          unit,
          heightValue,
          'appAckronym=RPM6000',
          'professionalEntered'
        )
      );
    }

    if (medicalInfo.weight) {
      const unit = weightUnit || 'kg';
      measurements.push(
        buildMeasurement(
          'weight',
          now,
          0,
          uid,
          unit,
          medicalInfo.weight,
          'appAckronym=RPM6000',
          'professionalEntered'
        )
      );
    }

    if (medicalInfo.dryWeight) {
      const unit = weightUnit || 'kg';
      measurements.push(
        buildMeasurement(
          'dry_weight',
          now,
          0,
          uid,
          unit,
          medicalInfo.dryWeight,
          'appAckronym=RPM6000',
          'professionalEntered'
        )
      );
    }

    return measurements;
  };

  const postBaselineMeasurements = async (
    uid: string,
    medicalInfo: IInvitationMedicalInfo,
    /** Submission only: the explicit height and weight save is the user asking again. */
    once = false
  ): Promise<boolean> => {
    const baselineMeasurements = buildBaselineMeasurements(uid, medicalInfo);
    if (!uid || baselineMeasurements.length === 0) return true;
    // Keyed on the values, not the uid alone: a correction before Try again is a new post.
    const sentKey = [
      uid,
      ...baselineMeasurements.map((m) => `${m.class}:${m.value}${m.unit}`)
    ].join('|');
    if (once) {
      if (baselinesSentKeyRef.current === sentKey) return true;
      baselinesSentKeyRef.current = sentKey;
    }
    try {
      await instance.post('/clinician/Measurement', { measurements: baselineMeasurements });
      return true;
    } catch {
      if (once) baselinesSentKeyRef.current = null;
      toast(
        <ToastNotificationContent
          icon={<WarningIcon className="icon" />}
          text="toastBaselineMeasurementsErrorKey"
        />,
        { type: 'error' }
      );
      return false;
    }
  };

  const saveHeightWeight = async (entered: IBodyMeasurementFields) => {
    const saved = await postBaselineMeasurements(patient?.uid || '', {
      primaryRpmReason: '',
      height: entered.height,
      heightInches: entered.heightInches,
      weight: entered.weight
    });
    if (!saved) throw new Error('Height and weight could not be saved');
  };

  /**
   * The order body, built once and used both to place the order and to ask what it would cost.
   *
   * Shared on purpose: a quote assembled separately would eventually be assembled differently, and
   * a price quoted for a slightly different order is worse than no price at all.
   */
  const buildOrderPayload = (
    values: IInvitation,
    uid?: string
  ): IBulkDeviceOrderRequest | IRegularDeviceOrderRequest | null => {
    const selectedSkus = Object.keys(values.devices);
    const accessorySkus = Object.keys(values.accessories);

    const resolveEffectiveOrderingId = (sku: string): string => {
      const entry = resolveCatalogEntry(sku, deviceAvailableToGroup);
      if (entry) return entry.deviceOrderingId;
      throw new Error(`No deviceOrderingId found for SKU: ${sku}`);
    };

    const shippingInfo = toDeviceOrderShippingInfo(values.shippingInfo);

    if (
      activeInviteType === InviteType.BULK_ORDER_DEVICES ||
      activeInviteType === InviteType.BULK_ORDER_ACCESSORIES
    ) {
      if (selectedSkus.length === 0 && accessorySkus.length === 0) return null;
      const mergedQuantities: Record<string, number> = { ...values.devices, ...values.accessories };
      const items: IBulkDeviceOrderItem[] = [...selectedSkus, ...accessorySkus].map((sku) => ({
        deviceOrderingId: resolveEffectiveOrderingId(sku),
        quantity: mergedQuantities[sku]
      }));
      const payload: IBulkDeviceOrderRequest = {
        orderType: 'bulk',
        items,
        gid: invitationGid,
        shippingInfo
      };
      return payload;
    }

    if (selectedSkus.length === 0) return null;

    const payload: IRegularDeviceOrderRequest = {
      orderType: 'regular',
      deviceOrderingIds: selectedSkus.map(resolveEffectiveOrderingId),
      uid: uid || patient?.uid || '',
      gid: invitationGid,
      shippingInfo
    };
    if (values.shippingInfo.useAsPatientAddress) {
      payload.shippingAddressIsPatientAddress = true;
    }
    // Standard is the API's default everywhere; Expedited is refused on a cart with no Amazon SKU
    // and on bulk orders, so the field is only sent where a choice exists.
    if (cartHasAmazonItem(values.devices, deviceAvailableToGroup)) {
      payload.shippingSpeed = values.shippingSpeed;
    }
    return payload;
  };

  const postDeviceOrderForSkus = (values: IInvitation, uid?: string): Promise<unknown> => {
    const payload = buildOrderPayload(values, uid);
    if (!payload) return Promise.resolve();
    return postDeviceOrder({ data: payload });
  };

  /** A patient created by a submission whose device order then failed; see handleSubmit. */
  const createdPatientUidRef = useRef<string | null>(null);
  /**
   * The patient and values already posted, set when the request goes out so Try again cannot post
   * them twice, and cleared on failure so a retry does post them.
   */
  const baselinesSentKeyRef = useRef<string | null>(null);
  /** The same gate prices the real order, so one the quote has refused cannot be placed. */
  const [quoteRefused, setQuoteRefused] = useState<string | null>(null);

  const handleSubmit = (values: IInvitation) => {
    if (submitLoading) return;
    if (activeInviteType === InviteType.ORDER_PATIENT_DEVICE) {
      setInvitation({});
      postDeviceOrderForSkus(values).then(() => {
        if (values.shippingInfo.useAsPatientAddress) {
          dispatch(updatePatient({ address: values.shippingInfo.address }));
        }
        if (patient && fallback) fallback(true);
      });
    } else if (
      activeInviteType === InviteType.BULK_ORDER_DEVICES ||
      activeInviteType === InviteType.BULK_ORDER_ACCESSORIES
    ) {
      setInvitation({});
      postDeviceOrderForSkus(values).then((response) => {
        // The modal used to close itself here, straight back to a device list filtered to
        // Assigned, which showed "No devices match your search criteria". Charles: "Where are the
        // 50 devices I ordered: success or failure?" (QA v26.10, #5). The order is confirmed on
        // screen now, and the practice closes it when it has read it.
        setOrderResult((response as { data?: IDeviceOrderResponse })?.data ?? null);
        refreshCredits();
      });
    } else {
      const currentPatient = handlePatientParams(values);
      const data = {
        patients: [currentPatient],
        strictValidation: '1',
        downloadPO: '0',
        sendPatientEmail: '1'
      };
      setInvitation(data);
      /**
       * The patient is created first and the device order follows, so a failed order used to leave
       * the patient behind — and Try again then failed on the patient already existing, with the
       * device never ordered and no way forward but a new username (Charles #15, 2026-09-11).
       *
       * A patient we already created is remembered for the rest of this form, so a retry resumes at
       * the order instead of re-creating them. Cleared once the whole submission succeeds.
       */
      const alreadyCreated = createdPatientUidRef.current;
      const createPatient = alreadyCreated
        ? Promise.resolve({
            data: { patients: { [values.patientInfo.username || '']: { uid: alreadyCreated } } }
          })
        : postPatientsInfo({ data });
      createPatient.then((response) => {
        const username = values.patientInfo.username || '';
        const newPatientUid = response?.data?.patients?.[username]?.uid;
        if (newPatientUid) createdPatientUidRef.current = newPatientUid;
        postBaselineMeasurements(newPatientUid || '', values.medicalInfo, true);
        if (newPatientUid && Object.keys(values.devices).length > 0) {
          /**
           * The order's own failure has to reach the result screen. A toast here meant an
           * organisation with no credits saw "something went wrong" and a success panel, while the
           * identical order placed in bulk got the exact shortfall and a link to Billing (Alex,
           * 2026-09-11). The 402 body carries the numbers; the screen already knows how to render
           * them, and it was only ever this branch that swallowed it.
           *
           * The patient exists by now either way. `createdPatientUidRef` is what makes Try again
           * resume at the order rather than re-create them, so it is deliberately not cleared here.
           */
          postDeviceOrderForSkus(values, newPatientUid)
            .then(() => {
              createdPatientUidRef.current = null;
              if (patient && fallback) fallback(true);
            })
            .catch(() => undefined);
          return;
        }
        createdPatientUidRef.current = null;
        if (patient && fallback) fallback(true);
      });
    }
  };

  const validation = (values: IInvitation, activeStepIndex: number) => {
    const orderedSteps = buildOrderedSteps(null);
    switch (orderedSteps[activeStepIndex].name) {
      case InvitationStep.SELECT_GROUP_AND_TYPE: {
        const errors: any = {};
        if (!values.gid) errors.error = true;
        return errors;
      }
      case InvitationStep.CARE_PROGRAM: {
        const errors: any = {};
        if (values.patientCareProgram.length === 0) {
          errors.error = true;
          errors.patientCareProgram = intl.formatMessage({ id: 'selectOneProgramKey' });
        }
        if (
          programs.filter(
            (prog) =>
              prog.type === CareProgramTypes.MONITORING &&
              values.patientCareProgram.includes(prog.cpid)
          ).length > 1
        ) {
          errors.error = true;
          errors.patientCareProgram = intl.formatMessage({ id: 'multipleProgramsKey' });
        }
        return errors;
      }
      case InvitationStep.DEMOG_INFO: {
        const errors: any = { patientInfo: {} };
        values.patientInfo.isRequiredField = false;
        if (!values.patientInfo.firstName?.trim())
          errors.patientInfo.firstName = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        if (!values.patientInfo.lastName?.trim())
          errors.patientInfo.lastName = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        if (!values.patientInfo.username) {
          errors.patientInfo.username = intl.formatMessage({ id: 'FieldNotEmptyKey' });
        } else if (
          !regex.email(values.patientInfo.username) &&
          !regex.username(values.patientInfo.username)
        ) {
          errors.patientInfo.username = intl.formatMessage({ id: 'FieldIncorrectFormatKey' });
        }

        if (values.patientInfo.email_address && !regex.email(values.patientInfo.email_address)) {
          errors.patientInfo.email_address = intl.formatMessage({ id: 'FieldIncorrectFormatKey' });
        }

        if (!values.patientInfo.bday) {
          errors.patientInfo.bday = intl.formatMessage({ id: 'fieldEmptyOrIncorrectKey' });
        }

        const ssnRegex = new RegExp(intl.formatMessage({ id: 'ssnValidationRegex' }));
        if (values.patientInfo.ssn) {
          const ssnWithXs = 'XXX-XX-' + values.patientInfo.ssn;
          if (!ssnWithXs?.match(ssnRegex))
            errors.patientInfo.ssn = intl.formatMessage({ id: 'ssnErrorKey' });
        }

        if (!values.patientInfo.gender)
          errors.patientInfo.gender = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        if (!values.patientInfo.locale)
          errors.patientInfo.locale = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        if (isMRNMandatory && !values.patientInfo.mrn) {
          errors.patientInfo.mrn = intl.formatMessage({ id: 'FieldNotEmptyKey' });
        }

        if (_isEmpty(errors.patientInfo)) delete errors.patientInfo;

        return errors;
      }
      case InvitationStep.DEVICES:
      case InvitationStep.ACCESSORIES: {
        const errors: any = {};
        const allDeviceQtys = [
          ...Object.values(values.devices),
          ...Object.values(values.accessories)
        ];
        if ((!allDeviceQtys.length || allDeviceQtys.some((x) => x < 1)) && isAddDevice) {
          errors.error = true;
        }
        return errors;
      }
      case InvitationStep.MEDICAL_INFORMATION: {
        const errors: any = { medicalInfo: {} };
        if (values.medicalInfo.height) {
          const height = parseFloat(values.medicalInfo.height);
          if (height < 0) {
            errors.medicalInfo.height = intl.formatMessage({ id: 'invalidKey' });
          }
        }
        if (values.medicalInfo.weight) {
          const weight = parseFloat(values.medicalInfo.weight);
          if (weight < 0) {
            errors.medicalInfo.weight = intl.formatMessage({ id: 'invalidKey' });
          }
        }
        if (values.medicalInfo.dryWeight) {
          const dryWeight = parseFloat(values.medicalInfo.dryWeight);
          if (dryWeight < 0) {
            errors.medicalInfo.dryWeight = intl.formatMessage({ id: 'invalidKey' });
          }
        }
        if (values.medicalInfo.heightInches) {
          const inches = parseInt(values.medicalInfo.heightInches, 10);
          if (inches < 0 || inches > 11) {
            errors.medicalInfo.heightInches = intl.formatMessage({
              id: 'invalidKey'
            });
          }
        }
        if (_isEmpty(errors.medicalInfo)) delete errors.medicalInfo;
        return errors;
      }
      case InvitationStep.SHIPPING_INFO: {
        const errors: any = { shippingInfo: { address: {} } };

        if (!values.shippingInfo.firstName || !values.shippingInfo.firstName.trim())
          errors.shippingInfo.firstName = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        if (!values.shippingInfo.lastName || !values.shippingInfo.lastName.trim())
          errors.shippingInfo.lastName = intl.formatMessage({ id: 'FieldNotEmptyKey' });

        const validationFields = ['street_address_line_1', 'city', 'state_province', 'postal_code'];
        validationFields.forEach((x) => {
          if (!values.shippingInfo.address[x]) {
            if (x !== 'state_province' || countryCode !== 'SWE') {
              errors.shippingInfo.address[x] = intl.formatMessage({ id: 'FieldNotEmptyKey' });
            }
          }
        });

        const isBulkOrder =
          activeInviteType === InviteType.BULK_ORDER_DEVICES ||
          activeInviteType === InviteType.BULK_ORDER_ACCESSORIES;
        if (isBulkOrder && (!values.shippingInfo.email || !values.shippingInfo.email.trim())) {
          errors.shippingInfo.email = intl.formatMessage({ id: 'FieldNotEmptyKey' });
        } else if (values.shippingInfo.email && !regex.email(values.shippingInfo.email)) {
          errors.shippingInfo.email = intl.formatMessage({ id: 'FieldIncorrectFormatKey' });
        }

        if (_isEmpty(errors.shippingInfo.address)) delete errors.shippingInfo.address;
        if (_isEmpty(errors.shippingInfo)) delete errors.shippingInfo;

        return errors;
      }
      default:
        return {};
    }
  };

  interface IConfirmationProps {
    changeStep: any;
  }

  const Confirmation = ({ changeStep }: IConfirmationProps) => {
    const formik = useFormikContext<IInvitation>();
    /**
     * One extractor for both backends: the 7k answers with `errors` and the microservices with
     * `message`, and reading the 7k's straight into `formatMessage({ id })` threw the server's
     * actual complaint away (QA v26.10, #16). Some endpoints do answer with a translation key, so
     * a value we recognise as one is still translated; anything else is shown as it came.
     */
    const rawErrorMessage = getApiErrorMessage(submitError, '');
    const errorMessage = rawErrorMessage
      ? intl.messages[rawErrorMessage]
        ? intl.formatMessage({ id: rawErrorMessage })
        : rawErrorMessage
      : intl.formatMessage({ id: 'bulkOrderErrorKey' });
    const errorStatusCode = (errorAssign || errorDeviceOrder)?.response?.status;
    const insufficientCredit: IBillingInsufficientCreditError | null =
      errorDeviceOrder?.response?.status === 402 ? errorDeviceOrder.response.data : null;
    const taxUnavailable =
      errorDeviceOrder?.response?.status === 503 &&
      errorDeviceOrder.response.data?.code === 'TAX_ESTIMATE_UNAVAILABLE';
    const successMessage =
      activeInviteType === InviteType.BULK_ORDER_DEVICES
        ? 'bulkOrderSuccessKey'
        : activeInviteType === InviteType.ORDER_PATIENT_DEVICE
          ? 'inviteDeviceSentMsg'
          : '24hConfirmationEmailKey';
    const username = formik.values.patientInfo.username || 0;
    const onboardingWarning = isAddDevice
      ? false
      : submitData?.patients[username]?.onboardingWarning;

    /** What the form actually ordered, named from the catalogue the wizard was built from. */
    const orderedLines = [
      ...Object.entries(formik.values.devices || {}),
      ...Object.entries(formik.values.accessories)
    ]
      .filter(([, quantity]) => Number(quantity) > 0)
      .map(([sku, quantity]) => ({
        sku,
        quantity: Number(quantity),
        name: deviceNameForSku(intl, deviceAvailableToGroup, sku)
      }));

    /**
     * What this order is holding, read from the balance rather than recomputed: the reservation
     * the gate made is the authority on it, and the portal must not do its own arithmetic on
     * prices it deliberately does not show.
     */
    const orderCreditsHeld =
      balance?.reservations?.find(
        (reservation) => reservation.orderDisplayId === orderResult?.displayId
      )?.amountMinor ?? null;

    const reset = (formik: any, redirect = false) => {
      formik.resetForm({
        values: { ...defaultInvitation }
      });
      if (redirect) {
        navigate(getPath().dashboard.patients);
      } else {
        setInvitation(null);
        setInvitationGid('');
        changeStep(formik, 0);
      }
    };

    return (
      <Row className="justify-content-center" data-test="confirm">
        <Col md={6} lg={8}>
          {submitLoading ? (
            <Loading />
          ) : submitError ? (
            <Status status="error">
              <>
                {insufficientCredit ? (
                  <>
                    <h2 data-test="invite-error-message">
                      {intl.formatMessage({ id: 'billingInsufficientCreditTitleKey' })}
                    </h2>
                    <p className="mt-4" data-test="invite-error-insufficient-credit">
                      {intl.formatMessage(
                        { id: 'billingInsufficientCreditKey' },
                        {
                          required: formatMinor(insufficientCredit.requiredMinor),
                          available: formatMinor(insufficientCredit.availableMinor),
                          shortfall: formatMinor(insufficientCredit.shortfallMinor)
                        }
                      )}
                    </p>
                    <p className="mt-4 mb-5">
                      <Link
                        to={getPath().dashboard.billing(BillingTabs.OVERVIEW)}
                        data-test="invite-error-go-to-billing"
                      >
                        {intl.formatMessage({ id: 'billingGoToBillingKey' })}
                      </Link>
                    </p>
                  </>
                ) : taxUnavailable ? (
                  <>
                    <h2 data-test="invite-error-message">
                      {intl.formatMessage({ id: 'unexpectedErrorKey' })}
                    </h2>
                    <p className="mt-4 mb-5" data-test="invite-error-tax-unavailable">
                      {intl.formatMessage({ id: 'billingTaxUnavailableKey' })}
                    </p>
                  </>
                ) : (
                  <>
                    <h2 data-test="invite-error-message">
                      {intl.formatMessage({ id: 'unexpectedErrorKey' })}
                    </h2>
                    <p className="mt-4" data-test="invite-error-detail">
                      {errorMessage} / {errorStatusCode}
                    </p>
                    <p className="mt-4 mb-5">
                      {intl.formatMessage({ id: 'createPatientApiErrorKey' })}
                    </p>
                  </>
                )}
                <Button
                  variant="link"
                  onClick={() => {
                    changeStep(formik, 1);
                    setInvitation(null);
                  }}
                  data-test="invite-try-again"
                >
                  {intl.formatMessage({ id: 'tryAgainKey' })}
                </Button>
              </>
            </Status>
          ) : (
            <Status status="success">
              <>
                <h2 data-test="invite-success-message">
                  {intl.formatMessage({
                    id: isAddDevice ? 'orderPlacedKey' : 'invitationSentKey'
                  })}
                </h2>
                {!isAddDevice && (
                  <p className="mt-4">{`${patientNameFull} ${intl.formatMessage({
                    id: 'inviteSentMsg'
                  })}`}</p>
                )}
                {onboardingWarning && (
                  <Alert className="m-4" variant="danger">
                    {intl.formatMessage({ id: 'onboardingWarningKey' })}
                  </Alert>
                )}
                <p className="mt-4 mb-4">
                  {!isAddDevice ? (
                    <img src={questionIcon} className="me-2" />
                  ) : (
                    activeInviteType === InviteType.ORDER_PATIENT_DEVICE && `${patientNameFull} `
                  )}
                  {intl.formatMessage({
                    id: successMessage
                  })}
                </p>
                {/* What was ordered, what it cost and what is left: a success panel that says only
                    "done" sends the practice to the device list to work out whether it worked
                    (QA v26.10, #5). The ship date is deliberately absent, because nothing in the
                    order response carries one and an invented estimate is a promise. */}
                {orderResult && (
                  <div className="text-start mx-auto mb-5" style={{ maxWidth: 420 }}>
                    <div className="d-flex justify-content-between py-1 border-bottom">
                      <span className="text-secondary">
                        {intl.formatMessage({ id: 'orderNumberKey' })}
                      </span>
                      <span className="fw-bold" data-test="order-confirmation-id">
                        {orderResult.displayId}
                      </span>
                    </div>
                    {orderedLines.map((line) => (
                      <div
                        key={line.sku}
                        className="d-flex justify-content-between py-1 border-bottom"
                        data-test="order-confirmation-line"
                      >
                        <span className="text-secondary">{line.name}</span>
                        <span className="fw-bold">{line.quantity}</span>
                      </div>
                    ))}
                    {prepaidOrg && orderCreditsHeld !== null && (
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-secondary">
                          {intl.formatMessage({ id: 'orderCreditsHeldKey' })}
                        </span>
                        <span className="fw-bold" data-test="order-confirmation-held">
                          {formatMinor(orderCreditsHeld)}
                        </span>
                      </div>
                    )}
                    {prepaidOrg && balance && (
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-secondary">
                          {intl.formatMessage({ id: 'orderBalanceRemainingKey' })}
                        </span>
                        <span className="fw-bold" data-test="order-confirmation-balance">
                          {formatMinor(balance.availableMinor)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {orderResult && (
                  <Button
                    variant="primary"
                    onClick={() => fallback && fallback(true)}
                    data-test="order-confirmation-done"
                  >
                    {intl.formatMessage({ id: 'doneKey' })}
                  </Button>
                )}
                {!isAddDevice && !isEmbeddedWithPatient && (
                  <Button variant="link" onClick={() => reset(formik)} data-test="add-new-patient">
                    {intl.formatMessage({ id: 'addNewPatientKey' })}
                  </Button>
                )}
                {!isAddDevice && !isEmbeddedWithPatient && (
                  <Button
                    onClick={() => reset(formik, true)}
                    variant="link"
                    data-test="back-to-patient-list"
                  >
                    {intl.formatMessage({ id: 'backToPatientListKey' })}
                  </Button>
                )}
              </>
            </Status>
          )}
        </Col>
      </Row>
    );
  };

  const buildOrderedSteps = (changeStep: any): PageStep[] => {
    let steps: PageStep[] = [];
    // `submitData` is the patient-create response, which a stock order never produces. Without
    // `orderResult` the wizard would fall through to the form again now that it no longer closes
    // itself on success (QA v26.10, #5).
    const confirmationScreen =
      (submitLoading || submitData || orderResult || submitError) && invitation;
    if (!isAddDevice) {
      steps = steps.concat([
        {
          name: InvitationStep.CARE_PROGRAM,
          page: <SelectPrograms gid={invitationGid} />,
          preventLocationChange: true
        },
        {
          name: InvitationStep.DEMOG_INFO,
          page: <DemographicInformation isMRNMandatory={isMRNMandatory} />,
          preventLocationChange: true
        },
        {
          name: InvitationStep.MEDICAL_INFORMATION,
          page: <MedicalInformation focusHeightRef={focusHeightRef} />,
          preventLocationChange: true
        },
        {
          name: InvitationStep.CARE_PLAN,
          page: <CarePlans setCarePlans={setCarePlans} />,
          preventLocationChange: true
        }
      ]);
      if (!skipGroupStep) {
        steps.unshift({
          name: InvitationStep.SELECT_GROUP_AND_TYPE,
          page: (
            <SelectGroupAndType
              isEMRPatient={emrpatientId ? true : false}
              dropdownGroups={dropdownGroups}
            />
          ),
          preventLocationChange: true
        });
      }
    }
    const medicalInfoStepIndex = steps.findIndex(
      (step) => step.name === InvitationStep.MEDICAL_INFORMATION
    );
    const heightWeightMode =
      activeInviteType === InviteType.ORDER_PATIENT_DEVICE
        ? 'inline'
        : !isAddDevice
          ? 'medicalStep'
          : undefined;
    steps = steps.concat([
      {
        name:
          activeInviteType === InviteType.BULK_ORDER_ACCESSORIES
            ? InvitationStep.ACCESSORIES
            : InvitationStep.DEVICES,
        page: (
          <Devices
            suggestedDevices={suggestedDevices}
            bulkOrderDevices={activeInviteType === InviteType.BULK_ORDER_DEVICES}
            bulkOrderAccessories={activeInviteType === InviteType.BULK_ORDER_ACCESSORIES}
            devices={orderableDevices}
            patientDevices={patientDevices || []}
            heightWeightMode={heightWeightMode}
            heightWeightLoading={bodyMeasuresLoading}
            bodyMeasurePrefill={bodyMeasurePrefill}
            prefillAppliedRef={bodyMeasurePrefillAppliedRef}
            onSaveHeightWeight={saveHeightWeight}
            onGoToMedicalInfo={(formik) => {
              if (!changeStep || medicalInfoStepIndex < 0) return;
              focusHeightRef.current = true;
              changeStep(formik, medicalInfoStepIndex);
            }}
          />
        ),
        preventLocationChange: true,
        skipStep: skipDevices,
        fieldNames: !isAddDevice ? ['devices'] : []
      },
      {
        name: InvitationStep.SHIPPING_INFO,
        page: (
          <ShippingInfo
            inviteType={activeInviteType}
            devices={orderableDevices}
            showAddressToggle={showAddressToggle}
            homeShippingCandidate={homeShippingCandidate}
            lastShippingCandidate={lastShippingCandidate}
          />
        ),
        preventLocationChange: true,
        skipStep:
          skipDevices || (skipShipping && activeInviteType !== InviteType.ORDER_PATIENT_DEVICE)
      },
      {
        name: InvitationStep.REVIEW_AND_CONFIRM,
        hideStepper: confirmationScreen,
        hideFooter: confirmationScreen,
        page: confirmationScreen ? (
          <Confirmation changeStep={changeStep} />
        ) : (
          <Review
            inviteType={activeInviteType}
            carePlans={carePlans}
            devices={orderableDevices}
            uid={patient?.uid}
            buildOrderPayload={buildOrderPayload}
            onQuoteRefused={setQuoteRefused}
          />
        ),
        preventLocationChange: !confirmationScreen
      }
    ]);
    return steps;
  };

  useEffect(() => {
    const suggestedGroup = new Set<DeviceTypeKey>();

    for (const plan of carePlans) {
      if (!savedCareplanTemplateIds.includes(plan.template_id)) continue;

      for (const task of plan.observationTasks ?? []) {
        addByMClass(task.mClass ?? '', suggestedGroup);
      }
      addByKeyword(plan.template_description ?? '', suggestedGroup);
    }

    const conditionKeys = [
      savedMedicalInfo?.primaryRpmReason,
      savedMedicalInfo?.secondaryRpmReason,
      ...(savedMedicalInfo?.conditions?.map((c) => c.value) ?? [])
    ].filter((k): k is string => Boolean(k));

    for (const key of conditionKeys) {
      addByKeyword(key, suggestedGroup);
    }

    const groupedDevices = deviceAvailableToGroup.reduce(
      (acc, curr) => {
        if (
          acc[curr.displayType] === undefined ||
          seriesToNum(acc[curr.displayType].series) < seriesToNum(curr.series)
        ) {
          acc[curr.displayType] = curr;
        }
        return acc;
      },
      {} as Record<DeviceTypeKey, IDeviceOrderingCatalogItem>
    );

    const suggestion: IDeviceOrderingCatalogItem[] = [];
    for (const groupKey of [...suggestedGroup]) {
      if (groupedDevices[groupKey]) {
        suggestion.push(groupedDevices[groupKey]);
      }
    }
    setSuggestedDevices(suggestion);
  }, [carePlans, deviceAvailableToGroup, savedMedicalInfo, savedCareplanTemplateIds]);

  useEffect(() => {
    if (
      activeInviteType === InviteType.ORDER_PATIENT_DEVICE &&
      !patientDevices &&
      !loadingPatientDevices &&
      !errorPatientDevices
    ) {
      getPatientdevices();
    }
  }, [
    getPatientdevices,
    activeInviteType,
    patientDevices,
    loadingPatientDevices,
    errorPatientDevices
  ]);

  /**
   * The step the footer button actually leads to.
   *
   * Only the `skipStep` **predicate** is consulted, because that is the only thing
   * `StepperPage.handleSubmit` consults when it advances. The `skipStep` *flag* on a step is a
   * different thing: it drives the stepper's display and backwards navigation, and for shipping it
   * is baked from `skipShipping`, which `switchStep` refreshes only on a step change. Arriving at
   * the devices step with nothing chosen sets it, so it still says "no devices" the moment someone
   * chooses one - and reading it here made the button announce a review screen that the press
   * itself would not go to (Tatiana, 2026-09-15).
   */
  const nextVisibleStep = (
    values: IInvitation,
    orderedSteps: PageStep[],
    fromIndex: number
  ): PageStep | null => {
    for (let index = fromIndex + 1; index < orderedSteps.length; index++) {
      if (skipStep(values, index, orderedSteps)) continue;
      return orderedSteps[index];
    }
    return null;
  };

  const getFooterNextLabel = (
    intl: ReturnType<typeof useIntl>,
    formik: FormikProps<IInvitation>,
    controls: FooterControls
  ): string => {
    const orderedSteps = buildOrderedSteps(null);
    const isDevicesStep =
      orderedSteps[controls.activeStepIndex]?.name === InvitationStep.DEVICES ||
      orderedSteps[controls.activeStepIndex]?.name === InvitationStep.ACCESSORIES;
    const hasSelection =
      Object.keys(formik.values.devices).length > 0 ||
      Object.keys(formik.values.accessories).length > 0;

    if (controls.isLastStep) return intl.formatMessage({ id: 'confirmKey' });
    // Nothing selected skips both this step's order and the shipping step after it.
    if (isDevicesStep && !hasSelection) return intl.formatMessage({ id: 'skipButtonKey' });
    /*
     * "Review & Confirm" only when Review is genuinely next.
     *
     * Selecting a device is exactly what stops the shipping step being skipped
     * (`skipStep` returns true for SHIPPING_INFO only when no device is chosen), so the devices
     * step promised a review screen in precisely the case where Shipping Information comes first
     * - the button said "Review & Confirm" and then asked for an address (Tatiana, 2026-09-15).
     */
    if (isDevicesStep) {
      const next = nextVisibleStep(formik.values, orderedSteps, controls.activeStepIndex);
      if (next?.name === InvitationStep.REVIEW_AND_CONFIRM)
        return intl.formatMessage({ id: 'onboardingReviewConfirmKey' });
    }
    return intl.formatMessage({ id: 'next' });
  };

  const renderFooterCenter = (
    formik: FormikProps<IInvitation>,
    controls: FooterControls
  ): React.ReactNode => {
    const orderedSteps = buildOrderedSteps(null);
    const isDevicesStep =
      orderedSteps[controls.activeStepIndex]?.name === InvitationStep.DEVICES ||
      orderedSteps[controls.activeStepIndex]?.name === InvitationStep.ACCESSORIES;
    const selectedSkus = isDevicesStep
      ? [...Object.keys(formik.values.devices), ...Object.keys(formik.values.accessories)]
      : [];
    if (selectedSkus.length === 0) return null;

    return (
      <>
        {selectedSkus.map((sku) => {
          const device = deviceAvailableToGroup.find((d) => d.sku === sku);
          if (!device) return null;
          return (
            <span
              key={sku}
              className="rounded-pill px-3 py-1"
              style={{ fontSize: 13, border: '1px solid #2ba3ef', backgroundColor: 'white' }}
            >
              {intl.formatMessage({ id: device.displayName, defaultMessage: device.displayName })}
            </span>
          );
        })}
      </>
    );
  };

  const renderFullPageFooter = (
    formik: FormikProps<IInvitation>,
    controls: FooterControls
  ): React.ReactNode => {
    const center = renderFooterCenter(formik, controls);
    return (
      <FloatingFooter
        formik={formik}
        onPrev={controls.onPrev}
        isFirstStep={controls.isFirstStep}
        nextLabel={getFooterNextLabel(intl, formik, controls)}
        renderCenter={center ? () => center : undefined}
        onHeightChange={setFooterHeight}
      />
    );
  };

  const renderModalFooter = (
    formik: FormikProps<IInvitation>,
    controls: FooterControls
  ): React.ReactNode => (
    <div
      className={`mt-4 pe-4 ${activeInviteType === InviteType.BULK_ORDER_DEVICES ? 'border-top pt-3' : ''}`}
    >
      <div className="d-flex">
        {!controls.isFirstStep && (
          <Button variant="link" onClick={controls.onPrev} data-test="prev-button">
            {intl.formatMessage({ id: 'prev' })}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={!formik.isValid || (controls.isLastStep && !!quoteRefused)}
          className="ms-auto"
          data-test="next-button"
        >
          {controls.isLastStep
            ? intl.formatMessage({ id: 'confirmKey' })
            : intl.formatMessage({ id: 'next' })}
        </Button>
      </div>
    </div>
  );

  const onProceedPrompt = (targetPath: string) => {
    if (matchPath(targetPath, getPath().dashboard.onboard)) {
      window.location.replace(getPath().dashboard.onboard);
    } else {
      navigate(targetPath);
    }
  };

  return (
    <Row className={`justify-content-center ${isAddDevice ? '' : ' mb-fixed-footer'}`}>
      <Col md={12} className="p-0">
        {!loadingEMRPatient && (
          <StepperPage
            activeInviteType={activeInviteType}
            buildOrderedSteps={buildOrderedSteps}
            defaultValues={defaultInvitation}
            handleValidation={validation}
            submitAction={handleSubmit}
            switchStep={switchStep}
            skipStep={skipStep}
            onProceedPrompt={onProceedPrompt}
            constrainToModal={isAddDevice}
            renderFooter={isAddDevice ? renderModalFooter : renderFullPageFooter}
            contentPaddingBottom={!isAddDevice ? footerHeight + 24 : undefined}
            stepperClassNames="p-0"
          />
        )}
      </Col>
    </Row>
  );
};
export default Onboard;
